import * as THREE from "three";
import { classifyAssemblyMesh } from "../../../engines/assemblyFilm/classify";
import { resolveAssemblyMeshName } from "../../../engines/assemblyFilm/meshName";
import type { AssemblyFilmStoryboard } from "../../../engines/assemblyFilm/storyboard";

/**
 * Posing the built sauna for one frame of the assembly film.
 *
 * The film never touches geometry or the engine's shared materials: it swaps
 * each mesh's `material` REFERENCE to one of two film-owned materials, moves
 * `position`, toggles `visible`, and restores all three exactly. That keeps
 * the shared-material contract (docs/notes/2026-04-12-shared-threejs-material-
 * libraries-must-be-lazy-and-disposable.md) and lets the film sit on top of a
 * live, dirty-tracked scene.
 */

export interface FilmPart {
	mesh: THREE.Mesh;
	name: string;
	step: number;
	/** Build order within its step (0-based) and the step's part count. */
	order: number;
	count: number;
	material: THREE.Mesh["material"];
	visible: boolean;
	/** What the film last wrote to `mesh.visible`; null before the first pose. */
	appliedVisible: boolean | null;
	basePosition: THREE.Vector3;
	/** World bounds in the settled pose. */
	worldBox: THREE.Box3;
	/** Rotates a world direction into the parent's local frame. */
	parentInverseQuaternion: THREE.Quaternion;
	parentScale: number;
}

export interface FilmPoseMaterials {
	ghost: THREE.Material;
	accent: THREE.Material;
}

/** Share of a step's timeline one part takes to settle. */
const PART_DURATION = 0.35;
/** Share of the timeline over which part start times are spread. */
const STAGGER_SPAN = 0.5;

/**
 * Collects every built mesh with its settled pose. Pass the previous
 * collection when re-collecting over a live scene: a mesh the film already
 * posed keeps its recorded base, unless someone else (a layer toggle) has
 * changed its visibility since the film last wrote it, in which case that
 * newer value becomes the base.
 */
export function collectFilmParts(
	root: THREE.Object3D,
	storyboard: AssemblyFilmStoryboard,
	previous: readonly FilmPart[] = [],
): FilmPart[] {
	const previousByMesh = new Map(previous.map((part) => [part.mesh, part]));
	for (const part of previous) {
		part.mesh.material = part.material;
		part.mesh.position.copy(part.basePosition);
	}
	root.updateWorldMatrix(true, true);
	const parts: FilmPart[] = [];
	const worldQuaternion = new THREE.Quaternion();
	const worldScale = new THREE.Vector3();
	const worldPosition = new THREE.Vector3();
	root.traverse((object) => {
		const mesh = object as THREE.Mesh;
		if (!mesh.isMesh) return;
		const name = resolveAssemblyMeshName(mesh);
		if (!name) return;
		const parent = mesh.parent ?? root;
		parent.matrixWorld.decompose(worldPosition, worldQuaternion, worldScale);
		const before = previousByMesh.get(mesh);
		const visible =
			before && before.appliedVisible === mesh.visible
				? before.visible
				: mesh.visible;
		mesh.visible = visible;
		parts.push({
			mesh,
			name,
			step: classifyAssemblyMesh(storyboard, name),
			order: 0,
			count: 1,
			material: mesh.material,
			visible,
			appliedVisible: null,
			basePosition: mesh.position.clone(),
			worldBox: new THREE.Box3().setFromObject(mesh),
			parentInverseQuaternion: worldQuaternion.clone().invert(),
			parentScale: worldScale.x || 1,
		});
	});

	// Within a step, build bottom-up, then along the wall: the order a
	// carpenter lays repeated pieces, and the order the arrows walk.
	const byStep = new Map<number, FilmPart[]>();
	for (const part of parts) {
		const list = byStep.get(part.step) ?? [];
		list.push(part);
		byStep.set(part.step, list);
	}
	const center = new THREE.Vector3();
	const key = (part: FilmPart) => {
		part.worldBox.getCenter(center);
		return [Math.round(center.y * 10), center.x + center.z * 0.5];
	};
	for (const list of byStep.values()) {
		list.sort((a, b) => {
			const [ay, ax] = key(a);
			const [by, bx] = key(b);
			return ay - by || ax - bx;
		});
		list.forEach((part, index) => {
			part.order = index;
			part.count = list.length;
		});
	}
	return parts;
}

function easeOutCubic(t: number): number {
	return 1 - (1 - t) ** 3;
}

/** 0 = not yet started, 1 = settled, for one part at step progress `p`. */
export function getFilmPartProgress(
	p: number,
	order: number,
	count: number,
): number {
	const start = count > 1 ? (order / (count - 1)) * STAGGER_SPAN : 0;
	return Math.min(1, Math.max(0, (p - start) / PART_DURATION));
}

export interface FilmPoseOptions {
	stepIndex: number;
	progress: number;
	reducedMotion: boolean;
	/** World direction parts arrive from (unit). */
	approach: THREE.Vector3;
	dropMeters: number;
	materials: FilmPoseMaterials;
}

const offset = new THREE.Vector3();

export function applyFilmPose(
	parts: FilmPart[],
	options: FilmPoseOptions,
): void {
	const {
		stepIndex,
		progress,
		reducedMotion,
		approach,
		dropMeters,
		materials,
	} = options;
	for (const part of parts) {
		const { mesh } = part;
		if (part.step > stepIndex) {
			mesh.visible = part.appliedVisible = false;
			mesh.position.copy(part.basePosition);
			mesh.material = part.material;
			continue;
		}
		if (part.step < stepIndex) {
			mesh.visible = part.appliedVisible = part.visible;
			mesh.position.copy(part.basePosition);
			mesh.material = materials.ghost;
			continue;
		}
		const local = reducedMotion
			? 1
			: getFilmPartProgress(progress, part.order, part.count);
		mesh.visible = part.appliedVisible = part.visible && local > 0;
		mesh.material = materials.accent;
		offset
			.copy(approach)
			.multiplyScalar(
				(dropMeters * (1 - easeOutCubic(local))) / part.parentScale,
			)
			.applyQuaternion(part.parentInverseQuaternion);
		mesh.position.copy(part.basePosition).add(offset);
	}
}

export function restoreFilmParts(parts: FilmPart[]): void {
	for (const part of parts) {
		part.mesh.material = part.material;
		part.mesh.visible = part.visible;
		part.mesh.position.copy(part.basePosition);
	}
}

export interface FilmArrow {
	/** World position of the arrow tip and the direction it points. */
	tip: THREE.Vector3;
	direction: THREE.Vector3;
	order: number;
	count: number;
}

/**
 * Up to `max` arrows for the active step, spread evenly over its parts, each
 * pointing at where its part will settle — just clear of the part's face
 * that looks toward the approach direction.
 */
export function planFilmArrows(
	parts: FilmPart[],
	stepIndex: number,
	approach: THREE.Vector3,
	max = 5,
): FilmArrow[] {
	const active = parts
		.filter((part) => part.step === stepIndex && part.visible)
		.sort((a, b) => a.order - b.order);
	if (active.length === 0) return [];
	const picks =
		active.length <= max
			? active
			: Array.from(
					{ length: max },
					(_, i) => active[Math.round((i * (active.length - 1)) / (max - 1))],
				);
	const size = new THREE.Vector3();
	return picks.map((part) => {
		const center = part.worldBox.getCenter(new THREE.Vector3());
		part.worldBox.getSize(size);
		const reach =
			Math.abs(approach.x) * size.x * 0.5 +
			Math.abs(approach.y) * size.y * 0.5 +
			Math.abs(approach.z) * size.z * 0.5;
		return {
			tip: center.addScaledVector(approach, reach + 0.06),
			direction: approach.clone().negate(),
			order: part.order,
			count: part.count,
		};
	});
}

/** Plan-view rectangles (world X/Z) per step, for the mini plan. */
export interface FilmPlanRect {
	id: string;
	step: number;
	x0: number;
	z0: number;
	x1: number;
	z1: number;
}

export function planFilmRects(parts: FilmPart[]): FilmPlanRect[] {
	return parts
		.filter((part) => part.visible && !part.worldBox.isEmpty())
		.map((part) => ({
			id: part.mesh.uuid,
			step: part.step,
			x0: part.worldBox.min.x,
			z0: part.worldBox.min.z,
			x1: part.worldBox.max.x,
			z1: part.worldBox.max.z,
		}));
}
